import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import * as _ from 'lodash';

import { Config } from 'shared/config/env.config';
import { contentHeaders } from 'common/headers';
import { SearchService } from 'shared/search/search.service';
import { SearchModel } from 'shared/search/search.model';
import { SortModel } from 'shared/search/sort.model';
import { importManagerStub } from './import-manager.stub';
import {catchError, map} from 'rxjs/operators';

@Injectable()
export class ImportManagerSearchService extends SearchService {

  // extendable fields:
  searchModel: SearchModel = new SearchModel();

  // implemented
  runSearch(brandId: string, searchModel: SearchModel, ...args: any[]): Observable<any> {
    /*
     *  If there is no sortModel set it to default
     */
    if (!searchModel.sortModel) {
      searchModel.sortModel = new SortModel();
      searchModel.sortModel.column = 'createdAt';
      searchModel.sortModel.sort = 'desc';
    }

    const status: string = (searchModel.statusModel) ? searchModel.statusModel.id.toString() : '';
    const searchUri = `/imports-upload/${brandId}`;
    const searchQueryString: string = this.getQueryString(searchModel);

     return this.http.get(`${Config.API}${searchUri}?${searchQueryString}`)
       .pipe(
           map( (res) => {
                this.updateData(res);
                return res;
            }),
           catchError(this.handleError.bind(this))
       );
  }

  // implemented
  updateData(data: any) {
    this.totalRecords = data.total;
    this.searchModel.currentPage = data.current_page;
    this.searchModel.lastPage = data.last_page;
    this.searchModel.searchId = _.get(data, 'id', null);

    data.data = _.map(data.data, this.formatRow);
    this.searchResults = data.data;
  }

  getQueryString(searchModel: SearchModel): string {
    const sortCol: string = (searchModel.sortModel) ? searchModel.sortModel.column : '';
    const sortDir: string = (searchModel.sortModel) ? searchModel.sortModel.sort : '';
    const queryString: string =
      'page=' + searchModel.currentPage +
      '&itemsPerPage=' + searchModel.itemsPerPage +
      '&search=' + searchModel.searchValue +
      '&sortCol=' + sortCol +
      '&sortDir=' + sortDir;

    return queryString;
  }

  getCompleted(brandId: string, fileIds: Array<string>): Observable<any> {
    if (fileIds.length) {
      const body = JSON.stringify({ ids: fileIds });

       return this.http.post(
         `${Config.API}/imports-upload/${brandId}/completed`,
         body,
         {headers: contentHeaders}
       )

       .pipe(map( (res) => {
          return _.map(res, this.formatRow);
       }), catchError(this.handleError.bind(this)));
    } else {
      return of([]);
    }
  }

  formatRow(row: any) {
    const output: any = {};
    output.id = row.id;
    output.filename = row.original_filename;
    output.parts_import_id = _.get(row, 'parts_import_id');
    output.item_count = 0;
    if (_.includes(['ITEMS', 'CUSTOM FIELDS'], _.get(row, 'parts_import.import_type'))) {
      output.item_count = row.parts_import.record_count;
    }
    output.app_count = (row.parts_import && row.parts_import.import_type === 'APPS') ? row.parts_import.record_count : 0;

    output.type = row.type;
    if (row.parts_import) {
      if (row.parts_import.import_type === 'ITEMS') {
        if (row.parts_import.filetype === 'XML') {
          output.type = 'PIES';
        } else if (row.parts_import.filetype === 'XLSX') {
          output.type = 'Item XLSX';
        }
      } else if (row.parts_import.import_type === 'APPS') {
        if (row.parts_import.filetype === 'XML') {
          output.type = 'ACES';
        } else if (row.parts_import.filetype === 'XLSX') {
          output.type = 'Fitment XLSX';
        }
      } else if (row.parts_import.import_type === 'CUSTOM FIELDS') {
        output.type = 'Custom Fields';
      }
    }
    output.import_type = _.get(row, 'parts_import.import_type', '');

    output.created_at = row.created_at;
    output.uploaded_by = row.uploaded_by;

    output.download = {
      file_id: null,
      completed: false,
      link: null,
    };
    output.download.file_id = row.id;
    output.download.completed = false;
    if (row.status === 2 || row.status === 3) {
      // 0 => HAS NO STARTED PROCESSING, 1 => PROCESSING, 2 => COMPLETED, 3 => HAS ERRORS
      output.download.completed = true;
    }
    output.download.link = row.url;

    output.report = {
      completed: false,
      status: null, // 1 - success, 2 - partial failure, 3 - total failure
      success_count: 0,
      fail_count: 0,
      errors: []
    };

    output.report.file_error_count = _.get(row, 'file_error_count', 0);

    if (row.status === 3) {
      output.report.status = 3; // Total Failure
    } else if (row.status === 2 && (
        (_.has(row, 'parts_import.error_count') && row.parts_import.error_count > 0) ||
        output.report.file_error_count)
              ) {
      output.report.status = 2; // Partial failure
    } else {
      output.report.status = 1; // Success
    }

    output.report.completed = false;
    if (row.status === 2 || row.status === 3) {
      // 0 => HAS NO STARTED PROCESSING, 1 => PROCESSING, 2 => COMPLETED, 3 => HAS ERRORS
      output.report.completed = true;
    }

    if (row.parts_import) {
      output.report.success_count = row.parts_import ? (row.parts_import.record_count - row.parts_import.error_count) : null;
      output.report.fail_count = _.get(row, 'parts_import.error_count');
      output.report.errors = [];
      if (_.has(row, 'parts_import.parts_import_errors')) {
        output.report.errors = _.map(_.get(row, 'parts_import.parts_import_errors'), 'error');
      }
    }


    return output;
  }
}
